import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ContactUsNew.css';
import { onMerchantContactRequestAPI } from '../../context-api/api-services/Onloadinfo';

/**
 * SingleCombo Merchant Portal: Contact Us
 * Styles live in ContactUsNew.css (same folder). No inline styles.
 *
 * iPad-friendly:
 *  - iPad and up (≥768px): navy info panel left, form right, both centered
 *  - Phones: panel stacked on top of the form
 *  - 16px+ inputs (no iOS zoom-on-focus), large touch targets, native select popover
 *  - Correct iPad keyboards via inputMode (tel / numeric / email)
 *
 * Props (all optional):
 *  - onSubmit(data): async fn that sends the callback request to your API. Throw to show an error.
 *  - signupUrl: merchant sign-up page. Form data is passed as query params.
 *  - onRedirectToSignup(data): override the redirect (e.g. React Router navigate). If set, signupUrl is ignored.
 *  - onBackToDashboard(): handler for "Back to dashboard"
 *  - redirectDelayMs: auto-redirect delay for new-account requests (default 2500)
 */

// Logged-out reasons
const REASONS = [
  { value: 'new_account', label: 'Open a new merchant account' },
  { value: 'application_status', label: 'Check my application status' },
  { value: 'login_help', label: "Can't log in / forgot password" },
  { value: 'account_locked', label: 'Account locked or suspended' },
  { value: 'verification', label: 'Not receiving verification code or email' },
  { value: 'ownership_change', label: 'Restaurant ownership or manager change' },
  { value: 'plans', label: 'Plans & pricing questions' },
  { value: 'other', label: 'Something else' },
];

const EMPTY = {
  restaurantName: '',
  contactPerson: '',
  phone: '',
  zip: '',
  reason: '',
  email: '',
  note: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WEB_DEVICE_ID_KEY = 'sc_merchant_contact_device_id';

const getWebDeviceId = () => {
  try {
    const existing = localStorage.getItem(WEB_DEVICE_ID_KEY);
    if (existing) return existing;
    const id = `web_${Math.random().toString(16).slice(2, 14)}`;
    localStorage.setItem(WEB_DEVICE_ID_KEY, id);
    return id;
  } catch {
    return 'web_contactus';
  }
};

const formatPhone = (raw) => {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const buildMerchantContactPayload = (values, reasonLabel) => ({
  restaurant_store_name: values.restaurantName.trim(),
  contact_person: values.contactPerson.trim(),
  designation: 'Contact',
  phone_number: values.phone.replace(/\D/g, ''),
  email: values.email.trim(),
  restaurant_address: values.zip ? `ZIP ${values.zip}` : '',
  how_did_you_hear_about_us: 'Contact Us page',
  device_id: getWebDeviceId(),
  team_to_contact: 'merchant-account-support',
  submitted_url: (typeof window !== 'undefined' ? window.location.pathname : '') || '/contactus',
  zipcode: values.zip || '',
  contact_reason: values.reason || '',
  note: values.note.trim(),
  reasonLabel: reasonLabel || '',
});

const validate = (v, { smsConsentRequired = false, smsConsent = false } = {}) => {
  const e = {};
  if (!v.restaurantName.trim()) e.restaurantName = 'Enter your restaurant name';
  if (!v.contactPerson.trim()) e.contactPerson = 'Enter a contact name';
  if (v.phone.replace(/\D/g, '').length !== 10) e.phone = 'Enter a 10-digit phone number';
  if (!/^\d{5}$/.test(v.zip)) e.zip = 'Enter a 5-digit ZIP code';
  if (!v.reason) e.reason = 'Pick a reason so the right person calls you';
  if (!EMAIL_RE.test(v.email.trim())) e.email = 'Enter a valid email address';
  if (smsConsentRequired && !smsConsent) {
    e.smsConsent = 'Please agree to receive the SMS verification code';
  }
  return e;
};

const SUBMIT_FAIL_MSG = "We couldn't send your request. Check your connection and try again.";

function getMerchantContactApiErrorMessage(response, fallback = SUBMIT_FAIL_MSG) {
  if (!response) return fallback;

  if (response.hasError) {
    const errData = response.error?.response?.data;
    if (errData?.customMessage) return errData.customMessage;
    if (errData?.CustomMessage) return errData.CustomMessage;
    if (errData?.errors?.[0]?.ErrorMessage) return errData.errors[0].ErrorMessage;
    if (!response.error?.response) {
      return 'Network error. Check your connection and try again.';
    }
    return fallback;
  }

  const payload = response.data || {};
  return (
    payload.customMessage || payload.CustomMessage || payload.errors?.[0]?.ErrorMessage || fallback
  );
}

function isMerchantContactApiSuccess(response) {
  if (!response || response.hasError) return false;
  const payload = response.data;
  if (!payload || typeof payload !== 'object') return false;
  return payload.success === true;
}

export default function ContactUsNew({
  onSubmit,
  signupUrl = '/merchant-contact',
  onRedirectToSignup,
  onBackToDashboard,
  redirectDelayMs = 2500,
}) {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [screen, setScreen] = useState('form'); // form | thanks | redirect
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const headingRef = useRef(null);
  const fieldRefs = useRef({});

  const isNew = values.reason === 'new_account';
  const reasonLabel = useMemo(
    () => REASONS.find((r) => r.value === values.reason)?.label ?? '',
    [values.reason]
  );

  // Move focus to the result heading when the screen changes (helps VoiceOver + keyboard)
  useEffect(() => {
    if (screen !== 'form') headingRef.current?.focus();
  }, [screen]);

  const goToSignup = () => {
    const data = { ...values, phone: values.phone.replace(/\D/g, '') };
    if (onRedirectToSignup) return onRedirectToSignup(data);
    const params = new URLSearchParams();
    if (data.restaurantName) params.set('name', data.restaurantName.trim());
    if (data.contactPerson) params.set('contact', data.contactPerson.trim());
    if (data.phone) params.set('phone', data.phone);
    if (data.zip) params.set('zip', data.zip);
    if (data.email) params.set('email', data.email.trim());
    const qs = params.toString();
    window.location.assign(qs ? `${signupUrl}?${qs}` : signupUrl);
  };

  // Auto-redirect for new-account requests
  useEffect(() => {
    if (screen !== 'redirect') return;
    const t = setTimeout(goToSignup, redirectDelayMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  const set = (key, val) => {
    setValues((v) => ({ ...v, [key]: val }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    if (submitError) setSubmitError('');
  };

  const CheckIsItForNewAccunt = reasonLabel === 'Open a new merchant account';

  const handleSubmit = async (e) => {
    e.preventDefault();
    // New merchant account → keep redirect-to-signup flow (no callback API)
    if (isNew) {
      setErrors({});
      setScreen('redirect');
      return;
    }
    const errs = validate(values, { smsConsentRequired: true, smsConsent });
    setErrors(errs);
    const firstBad = [
      'restaurantName',
      'contactPerson',
      'phone',
      'zip',
      'reason',
      'email',
      'smsConsent',
    ].find((k) => errs[k]);
    if (firstBad) {
      if (firstBad === 'smsConsent') {
        fieldRefs.current.smsConsent?.focus?.();
      } else {
        fieldRefs.current[firstBad]?.focus();
      }
      return;
    }
    setSending(true);
    setSubmitError('');

    const payload = buildMerchantContactPayload(values, reasonLabel);

    try {
      if (onSubmit) {
        await onSubmit(payload);
        setScreen('thanks');
        return;
      }

      const response = await onMerchantContactRequestAPI(payload);

      if (!isMerchantContactApiSuccess(response)) {
        setSubmitError(getMerchantContactApiErrorMessage(response));
        return;
      }

      setScreen('thanks');
    } catch (err) {
      const apiMsg = err?.response?.data?.customMessage || err?.response?.data?.CustomMessage;
      setSubmitError(apiMsg || SUBMIT_FAIL_MSG);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setValues(EMPTY);
    setErrors({});
    setSubmitError('');
    setSmsConsent(false);
    setScreen('form');
  };

  const backToDashboard = (e) => {
    if (onBackToDashboard) {
      e.preventDefault();
      onBackToDashboard();
    }
  };

  const fieldProps = (key) => ({
    id: `sc-${key}`,
    ref: (el) => (fieldRefs.current[key] = el),
    'aria-invalid': errors[key] ? 'true' : undefined,
    'aria-describedby': errors[key] ? `sc-${key}-err` : undefined,
    className: `sc-input${errors[key] ? ' sc-input--error' : ''}`,
  });

  const FieldError = ({ name }) =>
    errors[name] ? (
      <span id={`sc-${name}-err`} className='sc-error' role='alert'>
        {errors[name]}
      </span>
    ) : null;

  return (
    <div className='sc-root'>
      <main className='sc-main'>
        <aside className='sc-panel'>
          <div className='sc-panel-intro'>
            <span className='sc-kicker'>Merchant support</span>
            <h1 className='sc-title'>
              Need a hand?
              <br />
              We'll call you.
            </h1>
            <p className='sc-lede'>
              Tell us about your restaurant and what you need. A real person from our merchant team
              will reach out by phone.
            </p>
          </div>

          <ol className='sc-steps'>
            <li>
              <span className='sc-step-num'>1</span>
              <div>
                <strong>Share your details</strong>
                <span>Restaurant, contact name, phone and ZIP.</span>
              </div>
            </li>
            <li>
              <span className='sc-step-num'>2</span>
              <div>
                <strong>Pick a reason</strong>
                <span>So the right person calls you back.</span>
              </div>
            </li>
            <li>
              <span className='sc-step-num'>3</span>
              <div>
                <strong>We reach out</strong>
                <span>No tickets, no hold music.</span>
              </div>
            </li>
          </ol>

          <div className='sc-newbox'>
            <strong>New to SingleCombo?</strong>
            <span>
              Opening a merchant account has its own sign-up form. Choose “Open a new merchant
              account” and we'll take you there.
            </span>
          </div>
        </aside>

        <section className='sc-content'>
          {screen === 'form' && (
            <form className='sc-card' onSubmit={handleSubmit} noValidate>
              <div className='sc-card-head'>
                <h2 className='sc-h2'>Request a callback</h2>
                <p className='sc-sub'>All fields are required unless marked optional.</p>
              </div>

              <div className='sc-fields'>
                <div className='sc-row sc-row--even'>
                  <div className='sc-field'>
                    <label htmlFor='sc-restaurantName'>Restaurant name</label>
                    <input
                      {...fieldProps('restaurantName')}
                      type='text'
                      autoComplete='organization'
                      autoCapitalize='words'
                      enterKeyHint='next'
                      placeholder="e.g. Rosa's Taqueria"
                      value={values.restaurantName}
                      onChange={(e) => set('restaurantName', e.target.value)}
                    />
                    <FieldError name='restaurantName' />
                  </div>

                  <div className='sc-field'>
                    <label htmlFor='sc-contactPerson'>Contact person</label>
                    <input
                      {...fieldProps('contactPerson')}
                      type='text'
                      autoComplete='name'
                      autoCapitalize='words'
                      enterKeyHint='next'
                      placeholder='e.g. Rosa Martinez'
                      value={values.contactPerson}
                      onChange={(e) => set('contactPerson', e.target.value)}
                    />
                    <FieldError name='contactPerson' />
                  </div>
                </div>

                <div className='sc-row'>
                  <div className='sc-field'>
                    <label htmlFor='sc-phone'>Phone number</label>
                    <input
                      {...fieldProps('phone')}
                      type='tel'
                      inputMode='tel'
                      autoComplete='tel-national'
                      enterKeyHint='next'
                      placeholder='(312) 555-0123'
                      value={values.phone}
                      onChange={(e) => set('phone', formatPhone(e.target.value))}
                    />
                    <FieldError name='phone' />
                  </div>
                  <div className='sc-field'>
                    <label htmlFor='sc-zip'>Restaurant ZIP code</label>
                    <input
                      {...fieldProps('zip')}
                      type='text'
                      inputMode='numeric'
                      pattern='[0-9]*'
                      autoComplete='postal-code'
                      enterKeyHint='next'
                      maxLength={5}
                      placeholder='60601'
                      value={values.zip}
                      onChange={(e) => set('zip', e.target.value.replace(/\D/g, '').slice(0, 5))}
                    />
                    <FieldError name='zip' />
                  </div>
                </div>

                <div className='sc-row sc-row--even'>
                  <div className='sc-field'>
                    <label htmlFor='sc-reason'>Reason for contacting us</label>
                    <div className='sc-select-wrap'>
                      <select
                        {...fieldProps('reason')}
                        className={`sc-input sc-select${errors.reason ? ' sc-input--error' : ''}${
                          values.reason ? '' : ' sc-select--empty'
                        }`}
                        value={values.reason}
                        onChange={(e) => set('reason', e.target.value)}
                      >
                        <option value='' disabled>
                          Select a reason
                        </option>
                        {REASONS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                      <Chevron className='sc-select-icon' />
                    </div>
                    <FieldError name='reason' />
                  </div>

                  <div className='sc-field'>
                    <label htmlFor='sc-email'>Email</label>
                    <input
                      {...fieldProps('email')}
                      type='email'
                      inputMode='email'
                      autoComplete='email'
                      autoCapitalize='none'
                      autoCorrect='off'
                      spellCheck={false}
                      enterKeyHint='next'
                      placeholder='you@restaurant.com'
                      value={values.email}
                      onChange={(e) => set('email', e.target.value)}
                    />
                    <FieldError name='email' />
                  </div>
                </div>

                {isNew && (
                  <div className='sc-hint' role='status'>
                    <InfoIcon />
                    <span>
                      New accounts use our merchant sign-up form. Tap continue and we'll carry over
                      what you've entered here.
                    </span>
                  </div>
                )}

                {!isNew && (
                  <div className='sc-field'>
                    <label htmlFor='sc-note'>
                      Anything else we should know? <span className='sc-optional'>(optional)</span>
                    </label>
                    <textarea
                      id='sc-note'
                      className='sc-input sc-textarea'
                      rows={2}
                      placeholder='Best time to call, order number, etc.'
                      value={values.note}
                      onChange={(e) => set('note', e.target.value)}
                    />
                  </div>
                )}
              </div>

              {!isNew && (
                <div className='sc-consents'>
                  <label className='sc-consent'>
                    <input
                      type='checkbox'
                      className='sc-consent-input'
                      checked={smsConsent}
                      onChange={(e) => {
                        setSmsConsent(e.target.checked);
                        if (errors.smsConsent) {
                          setErrors((prev) => ({ ...prev, smsConsent: undefined }));
                        }
                      }}
                      ref={(el) => (fieldRefs.current.smsConsent = el)}
                      aria-invalid={errors.smsConsent ? 'true' : undefined}
                      aria-describedby={errors.smsConsent ? 'sc-smsConsent-err' : undefined}
                    />
                    <span className='sc-consent-text'>
                      By checking this box, you agree to receive a one-time verification code via
                      SMS to confirm your phone number for account authentication. Consent is not a
                      condition of purchase. Message and data rates may apply. Reply STOP to opt
                      out and HELP for help.
                    </span>
                  </label>
                  <FieldError name='smsConsent' />
                </div>
              )}

              {submitError && (
                <p className='sc-submit-error' role='alert'>
                  {submitError}
                </p>
              )}

              <button
                type='submit'
                className='sc-btn sc-btn--primary'
                disabled={sending || (!isNew && !smsConsent)}
              >
                {sending ? (
                  <>
                    <span className='sc-spinner' aria-hidden='true' />
                    Sending request…
                  </>
                ) : isNew ? (
                  'Continue to merchant form'
                ) : (
                  'Request a callback'
                )}
              </button>
            </form>
          )}

          {screen === 'thanks' && (
            <div className='sc-card sc-card--center' aria-live='polite'>
              <div className='sc-badge sc-badge--light'>
                <CheckIcon />
              </div>
              <h2 className='sc-h2 sc-h2--big' tabIndex={-1} ref={headingRef}>
                Thanks, {values.contactPerson.trim() || values.restaurantName.trim()}!
              </h2>
              <p className='sc-body'>
                We've got your request. Someone from the SingleCombo merchant team will reach out to
                you at <strong>{values.phone}</strong>.
              </p>
              <dl className='sc-summary'>
                <div>
                  <dt>Reason</dt>
                  <dd>{reasonLabel}</dd>
                </div>
                <div>
                  <dt>ZIP code</dt>
                  <dd>{values.zip}</dd>
                </div>
              </dl>
              <div className='sc-actions'>
                <a
                  href='/merchant/dashboard'
                  className='sc-btn sc-btn--dark'
                  onClick={backToDashboard}
                >
                  Back to dashboard
                </a>
                <button type='button' className='sc-btn sc-btn--ghost' onClick={reset}>
                  Send another request
                </button>
              </div>
            </div>
          )}

          {screen === 'redirect' && (
            <div className='sc-card sc-card--center' aria-live='polite'>
              <div className='sc-badge sc-badge--dark'>
                <StoreIcon />
              </div>
              <h2 className='sc-h2 sc-h2--big' tabIndex={-1} ref={headingRef}>
                Let's get you set up
              </h2>
              <p className='sc-body'>
                Opening a merchant account uses a separate sign-up form for your menu, hours and
                payout details. We're taking you there now.
              </p>
              <div className='sc-redirecting'>
                <span className='sc-dots' aria-hidden='true'>
                  <i />
                  <i />
                  <i />
                </span>
                Redirecting to merchant sign-up…
              </div>
              <div className='sc-actions'>
                <button
                  type='button'
                  className='sc-btn sc-btn--ghost'
                  onClick={() => {
                    set('reason', '');
                    setScreen('form');
                  }}
                >
                  Picked the wrong reason
                </button>
                <button type='button' className='sc-btn sc-btn--primary' onClick={goToSignup}>
                  Go to sign-up now
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

/* ---------- Icons ---------- */

function Chevron({ className }) {
  return (
    <svg
      className={className}
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2.2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M6 9l6 6 6-6' />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      className='sc-hint-icon'
    >
      <circle cx='12' cy='12' r='9' />
      <path d='M12 11v5' />
      <path d='M12 8h.01' />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width='36'
      height='36'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#C2410C'
      strokeWidth='2.4'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M5 12.5l4.5 4.5L19 7.5' />
    </svg>
  );
}

function StoreIcon() {
  return (
    <svg
      width='34'
      height='34'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#FF9A52'
      strokeWidth='2.2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M3 10l1.5-5h15L21 10' />
      <path d='M4 10v10h16V10' />
      <path d='M3 10h18' />
      <path d='M10 20v-5h4v5' />
    </svg>
  );
}
